/**
 * useTokenSources Hook
 *
 * Provides token sources for the TokenPicker based on the current workflow context.
 * Extracts outputs from previous actions, parameters, and variables.
 * Enhanced to propagate schema information from operation manifests and swagger to tokens.
 */

import { useMemo } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { Token, TokenType, SchemaObject } from '@/types/valueSegment';
import { TokenType as TokenTypeValues, TokenBrandColors } from '@/types/valueSegment';
import type { TokenSource } from '@/types/tokenSource';
import type { OperationManifest, OperationManifestProperties } from '@/types/operationManifest';
import type { SwaggerSpec } from '@/types/swagger';
import type { NodeConnectorInfo } from '@/types/connections';
import { getTokensForOperation } from '@/services/outputTokenService';
import { getSchemaDisplayTitle } from '@/utils/schema';
import type { ScopeContext } from '@/stores/nodeEditorStore';
import foreachManifestDef from '@/data/manifests/foreach';
import {
  flattenAllActions,
  getScopeAwarePredecessors,
} from '@/utils/workflow';
import type { Action } from '@/types/workflowDefinition';
import { useGetConnectorInfo } from '@/contexts';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';

// Stable empty objects to avoid creating new references
const EMPTY_ACTIONS: Record<string, unknown> = {};
const EMPTY_TRIGGERS: Record<string, unknown> = {};
const EMPTY_PARAMETERS: Record<string, { type?: string; description?: string } | undefined> = {};

// Type for action with runAfter
interface ActionWithRunAfter {
  type?: string;
  runAfter?: Record<string, string[]>;
  inputs?: Record<string, unknown>;
}

/**
 * Extract the action name from a node ID by stripping prefixes.
 * Node IDs have format: "action-ActionName" or "trigger-TriggerName".
 * Nested actions use a path form like "action-Foreach/HTTP" — we want
 * just the leaf name (action names are workflow-globally unique).
 */
function extractActionName(nodeId: string | undefined): string | undefined {
  if (!nodeId) return undefined;
  let raw = nodeId;
  if (raw.startsWith('action-')) {
    raw = raw.slice('action-'.length);
  } else if (raw.startsWith('trigger-')) {
    raw = raw.slice('trigger-'.length);
  }
  const lastSlash = raw.lastIndexOf('/');
  return lastSlash >= 0 ? raw.slice(lastSlash + 1) : raw;
}

/** Escape a value for use inside single-quoted expression arguments */
function escapeExprArg(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/** Build Otto's JS optional-bracket accessor for a property key. */
function toOptionalBracketAccessor(propName: string): string {
  return `?.['${escapeExprArg(propName)}']`;
}

// Type for action with detached flag
interface ActionWithDetached {
  type?: string;
  runAfter?: Record<string, string[]>;
  inputs?: Record<string, unknown>;
  detached?: boolean;
}

// Type for trigger definition
interface TriggerDefinition {
  type?: string;
  kind?: string;
  inputs?: {
    schema?: SchemaObject;
    relativePath?: string;
    method?: string;
  };
  splitOn?: string;
}

/**
 * Check if a node is connected to the trigger (i.e., no detached ancestors).
 * A node is connected to the trigger if:
 * 1. It is not detached itself
 * 2. None of its ancestors (via runAfter) are detached
 * 3. The chain eventually leads back to the trigger (no runAfter = runs after trigger)
 *
 * For tools (nested inside agents), checks the parent agent's connectivity.
 */
export function isConnectedToTrigger(
  currentNodeId: string | undefined,
  actions: Record<string, unknown>,
  parentAgentName?: string | undefined
): boolean {
  // If editing a trigger, it's always "connected"
  if (currentNodeId?.startsWith('trigger-')) {
    return true;
  }

  // For tools, check the parent agent's connectivity instead
  // Tools inherit their connectivity from their parent agent
  if (parentAgentName) {
    return checkActionConnectivity(parentAgentName, actions);
  }

  const currentActionName = extractActionName(currentNodeId);

  if (!currentActionName) {
    // No action specified - assume connected
    return true;
  }

  return checkActionConnectivity(currentActionName, actions);
}

/**
 * Check if a specific action is connected to the trigger.
 * Uses BFS to traverse the runAfter dependency graph.
 */
function checkActionConnectivity(
  actionName: string,
  actions: Record<string, unknown>
): boolean {
  const action = actions[actionName] as ActionWithDetached | undefined;
  if (!action) {
    // Action not found - assume connected (might be a trigger or external)
    return true;
  }

  // If the action is detached, not connected to trigger
  if (action.detached) {
    return false;
  }

  // BFS to check all ancestors for detached flag
  const queue: string[] = [];
  const visited = new Set<string>();
  visited.add(actionName); // Mark current as visited

  // Start with direct predecessors from runAfter
  const runAfter = action.runAfter;
  if (runAfter && Object.keys(runAfter).length > 0) {
    Object.keys(runAfter).forEach(name => {
      if (!visited.has(name)) {
        queue.push(name);
        visited.add(name);
      }
    });
  } else {
    // No runAfter means this action runs directly after trigger
    return true;
  }

  // BFS through the dependency graph
  while (queue.length > 0) {
    const predecessorName = queue.shift()!;

    const predecessorAction = actions[predecessorName] as ActionWithDetached | undefined;
    if (!predecessorAction) continue;

    // If any ancestor is detached, the current node is not connected to trigger
    if (predecessorAction.detached) {
      return false;
    }

    if (predecessorAction.runAfter && Object.keys(predecessorAction.runAfter).length > 0) {
      Object.keys(predecessorAction.runAfter).forEach(predName => {
        if (!visited.has(predName)) {
          queue.push(predName);
          visited.add(predName);
        }
      });
    }
    // If action has no runAfter, it's connected to trigger - continue checking others
  }

  // All paths lead back to trigger without hitting detached
  return true;
}


/**
 * Get all predecessor action names for a given action.
 *
 * Delegates to {@link getScopeAwarePredecessors} so nested actions
 * (Foreach/If/Switch/Until/Scope/Agent tools) see the right tokens —
 * sibling outputs from inside their own scope plus everything that ran
 * before each enclosing scope action.
 *
 * Adds trigger names when the current top-level action has no runAfter
 * (legacy behavior preserved). Triggers are otherwise rendered by their
 * own dedicated section in the picker.
 */
function getPredecessorActions(
  currentNodeId: string | undefined,
  actions: Record<string, unknown>,
  triggers: Record<string, unknown>
): Set<string> {
  // Extract the actual action name from the node ID (strip "action-" prefix)
  const currentActionName = extractActionName(currentNodeId);

  const predecessors = getScopeAwarePredecessors(
    currentActionName,
    actions as Record<string, Action>,
  );

  // Preserve legacy behavior: when a top-level action has no runAfter
  // (i.e., it runs immediately after the trigger), surface the trigger
  // names too. Harmless when the caller already iterates triggers.
  if (currentActionName) {
    const currentAction = (actions as Record<string, ActionWithRunAfter>)[currentActionName];
    const runAfter = currentAction?.runAfter;
    if (currentAction && (!runAfter || Object.keys(runAfter).length === 0)) {
      Object.keys(triggers).forEach(name => predecessors.add(name));
    }
  }

  return predecessors;
}

// =============================================================================
// TYPES
// =============================================================================

/**
 * Schema property type for agent parameters
 */
interface AgentSchemaProperty {
  type?: string;
  description?: string;
  title?: string;
}

/**
 * Agent parameter schema type
 */
export interface AgentParameterSchema {
  type?: string;
  properties?: Record<string, AgentSchemaProperty>;
  required?: string[];
}

export interface UseTokenSourcesOptions {
  /** Current node ID (to exclude from outputs) */
  currentNodeId?: string | undefined;
  /** Include trigger outputs */
  includeTrigger?: boolean | undefined;
  /** Include workflow parameters */
  includeParameters?: boolean | undefined;
  /** Include workflow variables */
  includeVariables?: boolean | undefined;
  /** Include agent parameters (for Agents) */
  includeAgentParameters?: boolean | undefined;
  /** Parent agent name (for agent parameter context) */
  parentAgentName?: string | undefined;
  /** Scope context when action is inside a ForEach or other scope container */
  scopeContext?: ScopeContext | undefined;
  /** Operation manifests by action name (for richer output schemas) */
  manifests?: Record<string, OperationManifest> | undefined;
  /** Swagger specs by action name (for managed connector output schemas) */
  swaggers?: Record<string, SwaggerSpec> | undefined;
  /** Agent parameter schema from the tool definition (overrides auto-detection) */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /**
   * App-level parameters (from `parameters.json`) to surface alongside any
   * workflow-definition parameters. Workflow-definition entries take
   * precedence on name collisions.
   */
  appLevelParameters?:
    | Record<string, { type?: string; description?: string; metadata?: { description?: string } } | undefined>
    | undefined;
}

export interface UseTokenSourcesResult {
  /** All available token sources */
  sources: TokenSource[];
  /** Flat list of all tokens */
  allTokens: Token[];
  /** Get tokens by type */
  getTokensByType: (type: TokenType) => Token[];
  /** Whether sources are loading */
  isLoading: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to get token sources for the TokenPicker
 */
export function useTokenSources({
  currentNodeId,
  includeTrigger = true,
  includeParameters = true,
  includeVariables = true,
  includeAgentParameters = true,
  parentAgentName,
  scopeContext,
  manifests = {},
  swaggers = {},
  agentParameterSchema,
  appLevelParameters,
}: UseTokenSourcesOptions = {}): UseTokenSourcesResult {
  // Get workflow data using selectors that return stable references
  // CRITICAL: Using stable empty objects avoids getSnapshot caching issues
  const actionsFromStore = useWorkflowStore((state) => state.workflow?.definition?.actions);
  const triggersFromStore = useWorkflowStore((state) => state.workflow?.definition?.triggers);
  const parametersFromStore = useWorkflowStore((state) => state.workflow?.definition?.parameters);

  // Use stable empty objects when undefined to prevent infinite loops
  const actions = actionsFromStore ?? EMPTY_ACTIONS;
  const triggers = triggersFromStore ?? EMPTY_TRIGGERS;
  const parameters = (parametersFromStore ?? EMPTY_PARAMETERS) as Record<string, { type?: string; description?: string } | undefined>;

  // No-op outside ConnectorProvider, so tests don't need to wrap the hook.
  const getConnectorInfo = useGetConnectorInfo();

  // Build token sources
  const sources = useMemo((): TokenSource[] => {
    const result: TokenSource[] = [];

    // Extract the action name from the node ID (strip prefixes like "action-" or "trigger-")
    const currentActionName = extractActionName(currentNodeId);

    // Get predecessor actions for the current node
    // Only show tokens from actions that execute BEFORE the current node
    const predecessorNames = getPredecessorActions(currentNodeId, actions, triggers);

    // Add trigger outputs (but not when editing the trigger itself)
    // Check if currentNodeId is a trigger name (using extracted name for lookup)
    const isEditingTrigger = currentActionName && triggers[currentActionName] !== undefined;

    if (includeTrigger && !isEditingTrigger) {
      const triggerEntries = Object.entries(triggers);
      if (triggerEntries.length > 0) {
        const triggerTokens: Token[] = triggerEntries.flatMap(([name, trigger]) => {
          const typedTrigger = trigger as TriggerDefinition;
          const triggerManifest = manifests[name];
          const { brandColor } = resolveTokenSourceDisplay(
            triggerManifest?.properties,
            getConnectorInfo(actionNameKey(name)),
            DEFAULT_TRIGGER_BRAND_COLOR,
          );

          // If we have a manifest with outputs, generate tokens from it
          if (triggerManifest?.properties?.outputs) {
            return generateTriggerTokensFromManifest(name, typedTrigger, triggerManifest, brandColor);
          }

          // Fallback to basic tokens for triggers without manifests
          return [
            {
              key: `trigger_${name}_body`,
              title: 'Body',
              name: 'body',
              tokenType: 'outputs' as TokenType,
              actionName: name,
              source: 'body',
              type: 'object',
              brandColor,
              value: `\${triggerBody()}`,
              description: 'The body of the trigger response',
            },
            {
              key: `trigger_${name}_headers`,
              title: 'Headers',
              name: 'headers',
              tokenType: 'outputs' as TokenType,
              actionName: name,
              source: 'headers',
              type: 'object',
              brandColor,
              value: `\${triggerOutputs()['headers']}`,
              description: 'The headers of the trigger response',
            },
          ];
        });

        if (triggerTokens.length > 0) {
          const firstName = triggerEntries[0]?.[0];
          const { brandColor, iconUri } = resolveTokenSourceDisplay(
            firstName ? manifests[firstName]?.properties : undefined,
            firstName ? getConnectorInfo(actionNameKey(firstName)) : undefined,
            DEFAULT_TRIGGER_BRAND_COLOR,
          );
          result.push({
            id: 'triggers',
            name: 'Triggers',
            brandColor,
            iconUri,
            tokens: triggerTokens,
          });
        }
      }
    }


    // Add action outputs - only from predecessor actions.
    // Walk the FLATTENED action tree so predecessors nested inside scopes
    // (Foreach/If/Switch/Until/Scope/Agent tools) are also surfaced.
    const flatActions = flattenAllActions(actions as Record<string, Action>);
    const actionEntries = Object.entries(flatActions).filter(
      ([name]) => name !== currentActionName && predecessorNames.has(name)
    );
    for (const [name, action] of actionEntries) {
      const typedAction = action as { type?: string; inputs?: Record<string, unknown> };
      const actionType = typedAction.type?.toLowerCase() ?? '';

      // Skip InitializeVariable actions - their outputs are accessed via variables() function
      if (actionType === 'initializevariable') {
        continue;
      }

      // Skip SetVariable - mutates an existing variable and produces no useful outputs.
      if (actionType === 'setvariable') {
        continue;
      }

      // Get manifest and swagger for this action if available
      const manifest = manifests[name] ?? null;
      const swagger = swaggers[name] ?? null;
      const manifestProps = manifest?.properties;
      const { brandColor, iconUri } = resolveTokenSourceDisplay(
        manifestProps,
        getConnectorInfo(actionNameKey(name)),
        getActionBrandColor(actionType),
      );

      // Extract operation ID from action inputs for swagger lookup
      const operationId = (typedAction.inputs?.['host'] as { operationId?: string } | undefined)?.operationId ?? null;

      let actionTokens: Token[] = [];

      // Get output schema from manifest (prefer body schema for managed connectors)
      const bodySchema = manifestProps?.outputs?.properties?.body as SchemaObject | undefined;
      const outputSchema = bodySchema ?? manifestProps?.outputs ?? (typedAction.inputs?.schema as SchemaObject | undefined);

      // If we have an output schema with properties, prefer hierarchical tokens
      // This provides the expandable nested structure for managed connectors
      // Only include root "body" wrapper if the manifest actually has a body property
      if (outputSchema && (outputSchema.properties || outputSchema.type === 'object' || outputSchema.type === 'array')) {
        const hasBodyProperty = !!bodySchema;
        actionTokens = generateTokensFromSchema(outputSchema, name, '', brandColor, 5, 0, hasBodyProperty);
      }

      // If no tokens from schema, use the output token service as fallback
      if (actionTokens.length === 0) {
        actionTokens = getTokensForOperation(manifest, swagger, operationId, {
          actionName: name,
          operationType: actionType,
          brandColor,
          isTrigger: false,
        });
      }

      if (actionTokens.length > 0) {
        result.push({
          id: `action_${name}`,
          name: manifestProps?.summary ?? formatDisplayName(name),
          brandColor,
          iconUri,
          tokens: actionTokens,
        });
      }
    }

    // Add workflow parameters. Workflow-definition parameters take precedence
    // over app-level (`parameters.json`) entries on name collision because the
    // workflow's own declaration is the more specific contract.
    if (includeParameters) {
      const merged = new Map<string, { type?: string | undefined; description?: string | undefined }>();
      if (appLevelParameters) {
        for (const [name, param] of Object.entries(appLevelParameters)) {
          if (!param) continue;
          merged.set(name, {
            type: param.type,
            description: param.description ?? param.metadata?.description,
          });
        }
      }
      for (const [name, param] of Object.entries(parameters)) {
        if (!param) continue;
        merged.set(name, { type: param.type, description: param.description });
      }

      const paramTokens: Token[] = Array.from(merged.entries()).map(([name, param]) => ({
        key: `param_${name}`,
        title: name,
        name,
        tokenType: 'parameter' as TokenType,
        type: param.type ?? 'string',
        brandColor: TokenBrandColors.parameter,
        value: `\${parameters('${escapeExprArg(name)}')}`,
        description: param.description ?? `Workflow parameter: ${name}`,
      }));

      if (paramTokens.length > 0) {
        result.push({
          id: 'parameters',
          name: 'Parameters',
          brandColor: TokenBrandColors.parameter,
          tokens: paramTokens,
        });
      }
    }

    // Add workflow variables from predecessor InitializeVariable actions
    if (includeVariables) {
      // Extract variables only from predecessor InitializeVariable actions.
      // Walk the flattened tree so variables initialized inside scopes
      // (Foreach/If/etc.) are also reachable when in scope.
      const predecessorVariables: Array<{ name: string; type: string; sourceAction: string }> = [];
      for (const [actionName, action] of Object.entries(flatActions)) {
        if (!predecessorNames.has(actionName)) continue;

        const actionDef = action as ActionWithRunAfter;
        if (actionDef.type?.toLowerCase() === 'initializevariable') {
          const inputs = actionDef.inputs as { variables?: Array<{ name: string; type?: string }> } | undefined;
          if (inputs?.variables && Array.isArray(inputs.variables)) {
            for (const varDef of inputs.variables) {
              if (varDef.name) {
                predecessorVariables.push({
                  name: varDef.name,
                  type: varDef.type?.toLowerCase() ?? 'string',
                  sourceAction: actionName,
                });
              }
            }
          }
        }
      }

      const variableTokens: Token[] = predecessorVariables.map((varInfo) => ({
        key: `var_${varInfo.name}`,
        title: varInfo.name,
        name: varInfo.name,
        tokenType: 'variable' as TokenType,
        type: varInfo.type,
        brandColor: TokenBrandColors.variable,
        value: `\${variables('${escapeExprArg(varInfo.name)}')}`,
        description: `Variable from ${varInfo.sourceAction}`,
      }));

      if (variableTokens.length > 0) {
        result.push({
          id: 'variables',
          name: 'Variables',
          brandColor: TokenBrandColors.variable,
          tokens: variableTokens,
        });
      }
    }

    // Add agent parameters (for tools within an Agent)
    // Generate tokens from the tool's agentParameterSchema if provided
    if (includeAgentParameters && parentAgentName) {
      const agentAction = actions[parentAgentName] as { type?: string } | undefined;
      if (agentAction?.type?.toLowerCase().includes('agent')) {
        let agentParamTokens: Token[] = [];

        // Use provided schema to generate tokens dynamically
        if (agentParameterSchema && agentParameterSchema.properties) {
          agentParamTokens = Object.entries(agentParameterSchema.properties).map(([propName, propSchema]) => ({
            key: `agent_param_${propName}`,
            title: getSchemaDisplayTitle(propSchema, propName),
            name: propName,
            tokenType: 'agentParameter' as TokenType,
            type: propSchema.type ?? 'string',
            brandColor: TokenBrandColors.agentParameter,
            value: `\${agentParameters('${escapeExprArg(propName)}')}`,
            description: propSchema.description ?? `Agent parameter: ${propName}`,
          }));
        }

        // Only add section if there are tokens
        if (agentParamTokens.length > 0) {
          result.push({
            id: 'agentParameters',
            name: 'Agent Parameters',
            brandColor: TokenBrandColors.agentParameter,
            tokens: agentParamTokens,
          });
        }
      }
    }

    // Add ForEach loop item tokens when action is inside a scope container
    if (scopeContext) {
      const loopTokens: Token[] = [];
      const allScopes = scopeContext.parentScopes ?? [{ actionName: scopeContext.parentActionName, type: 'Foreach' }];

      for (const scope of allScopes) {
        if (scope.type === 'Foreach') {
          // items('ForEachName') — current iteration item for this loop
          loopTokens.push({
            key: `foreach_item_${scope.actionName}`,
            title: `Item from "${scope.actionName}" loop`,
            name: 'items',
            tokenType: TokenTypeValues.ITEM,
            type: 'any',
            brandColor: TokenBrandColors[TokenTypeValues.ITEM],
            value: `\${items('${escapeExprArg(scope.actionName)}')}`,
            description: `Current iteration item from the "${scope.actionName}" ForEach loop. Use to access each element in the array.`,
            arrayDetails: { loopSource: scope.actionName },
          });

          // iterationIndexes('ForEachName') — current iteration index
          loopTokens.push({
            key: `foreach_index_${scope.actionName}`,
            title: `Index from "${scope.actionName}" loop`,
            name: 'iterationIndexes',
            tokenType: TokenTypeValues.ITERATIONINDEX,
            type: 'integer',
            brandColor: TokenBrandColors[TokenTypeValues.ITERATIONINDEX],
            value: `\${iterationIndexes('${escapeExprArg(scope.actionName)}')}`,
            description: `Current zero-based iteration index from the "${scope.actionName}" ForEach loop.`,
          });
        }
      }

      if (loopTokens.length > 0) {
        const firstForEachScope = allScopes.find(s => s.type === 'Foreach');
        const foreachManifest = firstForEachScope ? manifests[firstForEachScope.actionName] : undefined;
        result.push({
          id: 'foreachItems',
          name: 'ForEach Loop',
          brandColor: foreachManifest?.properties?.brandColor ?? foreachManifestDef.properties.brandColor,
          iconUri: foreachManifest?.properties?.iconUri ?? foreachManifestDef.properties.iconUri,
          tokens: loopTokens,
        });
      }
    }

    return result;
  }, [currentNodeId, actions, triggers, includeTrigger, includeParameters, includeVariables, includeAgentParameters, parentAgentName, scopeContext, manifests, getConnectorInfo, swaggers, appLevelParameters, parameters, agentParameterSchema]);

  // Flatten all tokens
  const allTokens = useMemo(() => {
    return sources.flatMap((source) => source.tokens);
  }, [sources]);

  // Get tokens by type
  const getTokensByType = useMemo(() => {
    return (type: TokenType): Token[] => {
      return allTokens.filter((token) => token.tokenType === type);
    };
  }, [allTokens]);

  return {
    sources,
    allTokens,
    getTokensByType,
    isLoading: false,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Format an action name as a display name
 */
function formatDisplayName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get brand color for an action type
 */
function getActionBrandColor(actionType: string): string {
  if (actionType.includes('http')) return '#0078d4';
  if (actionType.includes('agent')) return '#8764b8';
  if (actionType.includes('condition') || actionType.includes('if')) return '#00758f';
  if (actionType.includes('foreach') || actionType.includes('loop')) return '#00758f';
  if (actionType.includes('variable')) return '#00758f';
  if (actionType.includes('parse') || actionType.includes('json')) return '#107c10';
  if (actionType.includes('compose')) return '#4f6bed';
  return '#0078d4';
}

const DEFAULT_TRIGGER_BRAND_COLOR = '#0078d4';

/**
 * Resolve a token source's brand color and icon by preferring the manifest,
 * then the connector resource, then a type-based fallback.
 *
 * `swaggerOperationToManifest` sets `iconUri: ''` and a generic brandColor for
 * managed-connector operations because swagger doesn't carry that metadata —
 * the real values come from the connector resource (fetched separately by
 * `useWorkflowConnectors`). Use `||` so empty strings fall through.
 */
function resolveTokenSourceDisplay(
  manifestProps: OperationManifestProperties | undefined,
  connectorInfo: NodeConnectorInfo | undefined,
  fallbackBrandColor: string,
): { brandColor: string; iconUri: string | undefined } {
  return {
    brandColor: manifestProps?.brandColor || connectorInfo?.brandColor || fallbackBrandColor,
    iconUri: manifestProps?.iconUri || connectorInfo?.iconUri,
  };
}

/**
 * Generate tokens from a schema object's properties
 * Builds a tree structure with children for nested objects/arrays
 *
 * @param schema - The schema to generate tokens from
 * @param actionName - The name of the action producing these tokens
 * @param basePath - The base path for property access (e.g., "['prop']")
 * @param brandColor - Brand color for the tokens
 * @param maxDepth - Maximum depth for nested token generation (default: 5)
 * @param currentDepth - Current recursion depth (internal use)
 */
export function generateTokensFromSchema(
  schema: SchemaObject | undefined,
  actionName: string,
  basePath: string,
  brandColor: string,
  maxDepth: number = 5,
  currentDepth: number = 0,
  includeRootBody: boolean = true,
  implicitForeachExpression?: string,
  itemPathPrefix: string = ''
): Token[] {
  if (!schema) return [];
  if (currentDepth > maxDepth) return [];

  const tokens: Token[] = [];

  // Handle object properties
  if (schema.type === 'object' && schema.properties) {
    for (const [propName, propSchema] of Object.entries(schema.properties)) {
      const path = basePath ? `${basePath}['${escapeExprArg(propName)}']` : `['${escapeExprArg(propName)}']`;
      const itemPath = implicitForeachExpression
        ? `${itemPathPrefix}${toOptionalBracketAccessor(propName)}`
        : undefined;

      // Create token for this property
      const token: Token = {
        key: `action_${actionName}${path.replace(/[[\]']/g, '_')}`,
        title: getSchemaDisplayTitle(propSchema, propName),
        name: propName,
        tokenType: 'outputs' as TokenType,
        actionName,
        source: 'body',
        type: propSchema.type ?? 'string',
        brandColor,
        value: `\${body('${escapeExprArg(actionName)}')${path}}`,
        description: propSchema.description ?? `${propName} from ${actionName}`,
        schema: propSchema,
      };
      if (implicitForeachExpression && itemPath) {
        token.arrayDetails = {
          ...token.arrayDetails,
          implicitForeachExpression,
          implicitItemPath: itemPath,
        };
      }
      if (propSchema.format) {
        token.format = propSchema.format;
      }

      // Recursively generate children for nested objects
      if (propSchema.type === 'object' && propSchema.properties) {
        const childTokens = generateTokensFromSchema(
          propSchema,
          actionName,
          path,
          brandColor,
          maxDepth,
          currentDepth + 1,
          false, // Don't include root body for nested calls
          implicitForeachExpression,
          itemPath ?? ''
        );
        if (childTokens.length > 0) {
          token.children = childTokens;
        }
      }

      // Handle array items - add children for item properties
      if (propSchema.type === 'array' && propSchema.items) {
        const itemSchema = propSchema.items;
        if (itemSchema.type === 'object' && itemSchema.properties) {
          const childImplicitForeachExpression = implicitForeachExpression
            ? undefined
            : `\${body('${escapeExprArg(actionName)}')${path}}`;
          const childTokens = generateTokensFromSchema(
            itemSchema,
            actionName,
            path,
            brandColor,
            maxDepth,
            currentDepth + 1,
            false,
            childImplicitForeachExpression,
            ''
          );
          if (childTokens.length > 0) {
            token.children = childTokens;
          }
        }
      }

      tokens.push(token);
    }
  }

  // At the root level, wrap all tokens in a "body" token if requested
  if (currentDepth === 0 && includeRootBody && tokens.length > 0) {
    const bodyToken: Token = {
      key: `action_${actionName}_body`,
      title: 'body',
      name: 'body',
      tokenType: 'outputs' as TokenType,
      actionName,
      source: 'body',
      type: 'object',
      brandColor,
      value: `\${body('${escapeExprArg(actionName)}')}`,
      description: `Response body from ${actionName}`,
      children: tokens,
    };
    return [bodyToken];
  }

  // Handle array at root level
  if (schema.type === 'array' && schema.items) {
    const itemSchema = schema.items;
    if (itemSchema.type === 'object' && itemSchema.properties) {
      // Create a token for the array itself
      const arrayToken: Token = {
        key: `action_${actionName}${basePath || ''}_array`,
        title: getSchemaDisplayTitle(schema, 'items', 'Items'),
        name: 'items',
        tokenType: 'outputs' as TokenType,
        actionName,
        source: 'body',
        type: 'array',
        brandColor,
        value: `\${body('${escapeExprArg(actionName)}')${basePath || ''}}`,
        description: schema.description ?? `Array from ${actionName}`,
        schema: itemSchema,
      };

      // Add item properties as children
      const rootArrayImplicitForeachExpression = `\${body('${escapeExprArg(actionName)}')${basePath || ''}}`;
      const childTokens = generateTokensFromSchema(
        itemSchema,
        actionName,
        basePath,
        brandColor,
        maxDepth,
        currentDepth + 1,
        false,
        rootArrayImplicitForeachExpression,
        ''
      );
      if (childTokens.length > 0) {
        arrayToken.children = childTokens;
      }

      tokens.push(arrayToken);
    }
  }

  return tokens;
}

/**
 * Get type display name for a schema
 */
export function getSchemaTypeDisplay(schema: SchemaObject | undefined): string {
  if (!schema) return 'any';
  if (schema.type === 'array') {
    const itemType = schema.items?.type ?? 'any';
    return `${itemType}[]`;
  }
  return schema.type ?? 'any';
}

/**
 * Get the item schema for a splitOn expression.
 * When splitOn is configured (e.g., "@triggerOutputs()?.body?.['value']"),
 * navigates to the target array and returns its items schema.
 *
 * @param splitOn - The splitOn expression (e.g., "@triggerBody()?.['value']")
 * @param outputSchema - The original output schema from the manifest
 * @returns The items schema if splitOn targets an array, undefined otherwise
 */
function getSplitOnItemSchema(
  splitOn: string | undefined,
  outputSchema: SchemaObject | undefined
): SchemaObject | undefined {
  if (!splitOn || !outputSchema?.properties) return undefined;

  // Parse the splitOn expression to extract the path
  // Supported formats:
  // - @triggerBody() - targets body
  // - @triggerBody()?.['value'] - targets body.value
  // - @triggerOutputs()?.body?.['value'] - targets body.value
  const pathParts: string[] = [];

  // Check if it references body
  if (splitOn.includes('triggerBody()')) {
    pathParts.push('body');
  } else if (splitOn.includes('triggerOutputs()')) {
    // triggerOutputs() needs to navigate through outputs
    // Extract the property accessors
    const bodyMatch = splitOn.match(/\?\.body/);
    if (bodyMatch) {
      pathParts.push('body');
    }
  }

  // Extract property accessors like ?.['value'] (JS optional chaining) or
  // ?['value'] (legacy form emitted by older Otto/Logic Apps tooling).
  const bracketMatches = splitOn.matchAll(/\?\.?\[['"]([^'"]+)['"]\]/g);
  for (const match of bracketMatches) {
    if (match[1]) pathParts.push(match[1]);
  }

  const dotMatches = splitOn.matchAll(/\?\.([a-zA-Z_][a-zA-Z0-9_]*)/g);
  for (const match of dotMatches) {
    // Skip 'body' as we already handled it above
    if (match[1] && match[1] !== 'body') pathParts.push(match[1]);
  }

  if (pathParts.length === 0) return undefined;

  // Navigate through the schema following the path
  let currentSchema: SchemaObject | undefined = outputSchema;
  for (const part of pathParts) {
    currentSchema = currentSchema?.properties?.[part] as SchemaObject | undefined;
    if (!currentSchema) return undefined;
  }

  // If the target is an array, return its items schema
  if (currentSchema?.type === 'array' && currentSchema.items) {
    return currentSchema.items as SchemaObject;
  }

  return undefined;
}

/**
 * Generate tokens for a trigger from its manifest outputs
 * Handles special cases like Request trigger with path parameters and queries
 */
function generateTriggerTokensFromManifest(
  triggerName: string,
  trigger: TriggerDefinition,
  manifest: OperationManifest,
  brandColor: string
): Token[] {
  const tokens: Token[] = [];
  const outputs = manifest.properties.outputs as SchemaObject | undefined;

  if (!outputs?.properties) {
    return tokens;
  }

  for (const [propName, propSchema] of Object.entries(outputs.properties)) {
    const typedSchema = propSchema as SchemaObject;
    const title = getSchemaDisplayTitle(typedSchema, propName, formatDisplayName(propName));

    // Handle body - may have schema from trigger inputs or splitOn item schema
    if (propName === 'body') {
      // Check if splitOn is configured - if so, use the item schema for tokens
      const splitOnItemSchema = getSplitOnItemSchema(trigger.splitOn, outputs);

      const bodyToken: Token = {
        key: `trigger_${triggerName}_body`,
        title: 'Body',
        name: 'body',
        tokenType: 'outputs' as TokenType,
        actionName: triggerName,
        source: 'body',
        type: splitOnItemSchema ? (splitOnItemSchema.type ?? 'object') : 'object',
        brandColor,
        value: `\${triggerBody()}`,
        description: trigger.splitOn
          ? 'The body of the current split item'
          : 'The body of the trigger request',
      };

      // Priority: splitOn item schema > trigger input schema > body schema from manifest outputs.
      // The body schema fallback covers swagger-derived triggers (e.g. ApiConnection
      // batch triggers like OnNewEmailV3) whose response shape is already resolved
      // in the manifest — without it, body has no children and the deep token tree
      // (e.g. the 40 GraphClientReceiveMessage fields under `value`) is hidden.
      if (splitOnItemSchema?.properties) {
        // When splitOn is configured, generate tokens from the item schema
        const childTokens = generateTriggerSchemaTokens(
          triggerName,
          splitOnItemSchema,
          brandColor
        );
        if (childTokens.length > 0) {
          bodyToken.children = childTokens;
        }
      } else if (trigger.inputs?.schema?.properties) {
        // Fallback to trigger's input schema (Request-style triggers)
        const childTokens = generateTriggerSchemaTokens(
          triggerName,
          trigger.inputs.schema,
          brandColor
        );
        if (childTokens.length > 0) {
          bodyToken.children = childTokens;
        }
      } else if (typedSchema.properties) {
        // Fallback to the body schema itself from the manifest outputs
        const childTokens = generateTriggerSchemaTokens(
          triggerName,
          typedSchema,
          brandColor
        );
        if (childTokens.length > 0) {
          bodyToken.children = childTokens;
        }
      }

      tokens.push(bodyToken);
      continue;
    }

    // Handle headers
    if (propName === 'headers') {
      tokens.push({
        key: `trigger_${triggerName}_headers`,
        title: 'Headers',
        name: 'headers',
        tokenType: 'outputs' as TokenType,
        actionName: triggerName,
        source: 'headers',
        type: 'object',
        brandColor,
        value: `\${triggerOutputs()['headers']}`,
        description: 'The headers of the trigger request',
      });
      continue;
    }

    // Handle relativePathParameters - extract from relativePath input
    if (propName === 'relativePathParameters') {
      const pathParamToken: Token = {
        key: `trigger_${triggerName}_pathParams`,
        title: 'Path Parameters',
        name: 'relativePathParameters',
        tokenType: 'outputs' as TokenType,
        actionName: triggerName,
        source: 'outputs',
        type: 'object',
        brandColor,
        value: `\${triggerOutputs()['relativePathParameters']}`,
        description: 'Path parameters from the trigger URL',
      };

      // Extract path parameters from relativePath input (e.g., "/pets/{petId}" -> petId)
      const relativePath = trigger.inputs?.relativePath;
      if (relativePath) {
        const pathParamNames = extractPathParameters(relativePath);
        if (pathParamNames.length > 0) {
          pathParamToken.children = pathParamNames.map((paramName) => ({
            key: `trigger_${triggerName}_pathParam_${paramName}`,
            title: paramName,
            name: paramName,
            tokenType: 'outputs' as TokenType,
            actionName: triggerName,
            source: 'outputs',
            type: 'string',
            brandColor,
            value: `\${triggerOutputs()['relativePathParameters']['${escapeExprArg(paramName)}']}`,
            description: `Path parameter: ${paramName}`,
          }));
        }
      }

      tokens.push(pathParamToken);
      continue;
    }

    // Handle queries
    if (propName === 'queries') {
      tokens.push({
        key: `trigger_${triggerName}_queries`,
        title: 'Queries',
        name: 'queries',
        tokenType: 'outputs' as TokenType,
        actionName: triggerName,
        source: 'outputs',
        type: 'object',
        brandColor,
        value: `\${triggerOutputs()['queries']}`,
        description: 'Query string parameters from the trigger URL',
      });
      continue;
    }

    // Generic output property
    tokens.push({
      key: `trigger_${triggerName}_${propName}`,
      title,
      name: propName,
      tokenType: 'outputs' as TokenType,
      actionName: triggerName,
      source: 'outputs',
      type: typedSchema.type ?? 'string',
      brandColor,
      value: `\${triggerOutputs()['${escapeExprArg(propName)}']}`,
      description: typedSchema.description ?? `${title} from trigger`,
    });
  }

  return tokens;
}

/**
 * Extract path parameter names from a relative path template
 * E.g., "/pets/{petId}/items/{itemId}" -> ["petId", "itemId"]
 */
function extractPathParameters(relativePath: string): string[] {
  const paramRegex = /\{([^}]+)\}/g;
  const params: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = paramRegex.exec(relativePath)) !== null) {
    if (match[1]) {
      params.push(match[1]);
    }
  }

  return params;
}

/**
 * Generate child tokens from a trigger's body schema
 */
function generateTriggerSchemaTokens(
  triggerName: string,
  schema: SchemaObject,
  brandColor: string,
  basePath: string = '',
  currentDepth: number = 0,
  maxDepth: number = 5,
  implicitForeachExpression?: string,
  itemPathPrefix: string = ''
): Token[] {
  if (currentDepth > maxDepth) return [];
  if (!schema.properties) return [];

  const tokens: Token[] = [];

  for (const [propName, propSchema] of Object.entries(schema.properties)) {
    const typedSchema = propSchema as SchemaObject;
    const path = basePath ? `${basePath}['${escapeExprArg(propName)}']` : `['${escapeExprArg(propName)}']`;
    const itemPath = implicitForeachExpression
      ? `${itemPathPrefix}${toOptionalBracketAccessor(propName)}`
      : undefined;

    const token: Token = {
      key: `trigger_${triggerName}_body${path.replace(/[[\]']/g, '_')}`,
      title: getSchemaDisplayTitle(typedSchema, propName),
      name: propName,
      tokenType: 'outputs' as TokenType,
      actionName: triggerName,
      source: 'body',
      type: typedSchema.type ?? 'string',
      brandColor,
      value: `\${triggerBody()${path}}`,
      description: typedSchema.description ?? `${propName} from trigger body`,
      schema: typedSchema,
    };
    if (implicitForeachExpression && itemPath) {
      token.arrayDetails = {
        ...token.arrayDetails,
        implicitForeachExpression,
        implicitItemPath: itemPath,
      };
    }

    // Recursively generate children for nested objects
    if (typedSchema.type === 'object' && typedSchema.properties) {
      const childTokens = generateTriggerSchemaTokens(
        triggerName,
        typedSchema,
        brandColor,
        path,
        currentDepth + 1,
        maxDepth,
        implicitForeachExpression,
        itemPath ?? ''
      );
      if (childTokens.length > 0) {
        token.children = childTokens;
      }
    }

    // Handle array items
    if (typedSchema.type === 'array' && typedSchema.items) {
      const itemSchema = typedSchema.items as SchemaObject;
      if (itemSchema.type === 'object' && itemSchema.properties) {
        const childImplicitForeachExpression = implicitForeachExpression
          ? undefined
          : `\${triggerBody()${path}}`;
        const childTokens = generateTriggerSchemaTokens(
          triggerName,
          itemSchema,
          brandColor,
          path,
          currentDepth + 1,
          maxDepth,
          childImplicitForeachExpression,
          ''
        );
        if (childTokens.length > 0) {
          token.children = childTokens;
        }
      }
    }

    tokens.push(token);
  }

  return tokens;
}
